import { useAuth } from '@/contexts/AuthContext';
import { getPaydayInfo } from '@/lib/payday';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CalendarClock, Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PayFrequency } from '@/lib/types';

export function PaydayCountdown() {
  const { data, updateData } = useAuth();
  const [open, setOpen] = useState(false);

  const config = data.settings.paydayConfig;

  const info = useMemo(
    () => getPaydayInfo(data.transactions, config),
    [data.transactions, config],
  );

  // Config form state
  const [mode, setMode] = useState<'auto' | 'manual'>(config?.mode ?? 'auto');
  const [freq, setFreq] = useState<PayFrequency>(config?.frequency ?? 'fortnightly');
  const [nextDate, setNextDate] = useState(config?.nextDate ?? '');
  const [amount, setAmount] = useState(config?.amount?.toString() ?? '');

  function handleSave() {
    if (mode === 'manual' && (!nextDate || !freq)) return;
    const parsedAmount = parseFloat(amount);
    updateData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        paydayConfig: mode === 'auto'
          ? { mode: 'auto' as const }
          : {
              mode: 'manual' as const,
              frequency: freq,
              nextDate,
              amount: isNaN(parsedAmount) ? undefined : parsedAmount,
            },
      },
    }));
    setOpen(false);
  }

  function handleReset() {
    setMode('auto');
    setFreq('fortnightly');
    setNextDate('');
    setAmount('');
    updateData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        paydayConfig: { mode: 'auto' as const },
      },
    }));
    setOpen(false);
  }

  if (!info) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15">
                <CalendarClock className="h-4.5 w-4.5 text-accent-foreground" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Payday Countdown</h3>
                <p className="text-xs text-muted-foreground">Not enough data to detect</p>
              </div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Settings2 className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <ConfigForm
                  mode={mode} setMode={setMode}
                  freq={freq} setFreq={setFreq}
                  nextDate={nextDate} setNextDate={setNextDate}
                  amount={amount} setAmount={setAmount}
                  onSave={handleSave} onReset={handleReset}
                />
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    );
  }

  const freqLabel = info.frequency === 'weekly' ? 'Weekly' : info.frequency === 'fortnightly' ? 'Fortnightly' : 'Monthly';

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15">
              <CalendarClock className="h-4.5 w-4.5 text-accent-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Payday Countdown</h3>
              <p className="text-xs text-muted-foreground">
                {freqLabel} pay {info.isManual ? '(manual)' : '(detected)'}
              </p>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <ConfigForm
                mode={mode} setMode={setMode}
                freq={freq} setFreq={setFreq}
                nextDate={nextDate} setNextDate={setNextDate}
                amount={amount} setAmount={setAmount}
                onSave={handleSave} onReset={handleReset}
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-end justify-between">
          <div>
            <p className="font-display text-3xl font-bold text-primary">
              {info.daysUntil}
            </p>
            <p className="text-xs text-muted-foreground">
              {info.daysUntil === 1 ? 'day' : 'days'} until payday
            </p>
          </div>
          {info.estimatedAmount > 0 && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Est. amount</p>
              <p className="font-display text-lg font-semibold">{formatAUD(info.estimatedAmount)}</p>
            </div>
          )}
        </div>

        <p className="mt-2 text-xs text-muted-foreground">
          Next: {info.nextPayday.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
        </p>
      </CardContent>
    </Card>
  );
}

function ConfigForm({
  mode, setMode, freq, setFreq, nextDate, setNextDate, amount, setAmount,
  onSave, onReset,
}: {
  mode: 'auto' | 'manual';
  setMode: (m: 'auto' | 'manual') => void;
  freq: PayFrequency;
  setFreq: (f: PayFrequency) => void;
  nextDate: string;
  setNextDate: (d: string) => void;
  amount: string;
  setAmount: (a: string) => void;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Payday Settings</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div className="space-y-2">
          <Label>Detection mode</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as 'auto' | 'manual')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-detect from income</SelectItem>
              <SelectItem value="manual">Set manually</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {mode === 'manual' && (
          <>
            <div className="space-y-2">
              <Label>Pay frequency</Label>
              <Select value={freq} onValueChange={(v) => setFreq(v as PayFrequency)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Next pay date</Label>
              <Input
                type="date"
                value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Pay amount (optional)</Label>
              <Input
                type="number"
                placeholder="e.g. 2500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
          </>
        )}

        <div className="flex gap-2 pt-2">
          <Button className="flex-1" onClick={onSave}>
            Save
          </Button>
          <Button variant="outline" onClick={onReset}>
            Reset to Auto
          </Button>
        </div>
      </div>
    </>
  );
}
